import type { NotificationInboxItem } from '../types/notification.types';
import type {
  NotificationGroup,
  NotificationGroupAccumulator,
} from '../types/notification-group.types';

/** Groups canonical message notifications while keeping historical/system items independent. */
export const getNotificationGroupKey = (item: NotificationInboxItem): string =>
  JSON.stringify([
    item.tenantId,
    item.userId,
    item.type === 'MESSAGE' &&
    item.metadata?.message &&
    item.metadata.funnelConversationId
      ? [
          'conversation',
          item.metadata.funnelConversationId,
          Boolean(item.readAt),
        ]
      : ['notification', item.id],
  ]);

/** Original message time prevents retries from making an older preview look newest. */
export const getNotificationTime = (item: NotificationInboxItem): number =>
  Date.parse(item.metadata?.message?.createdAt ?? item.createdAt);

/** Groups loaded entries only; IDs are preserved for existing mark-read APIs. */
export const groupNotificationItems = (
  items: NotificationInboxItem[]
): NotificationGroup[] => {
  const groups = new Map<string, NotificationGroupAccumulator>();
  for (const item of items) {
    const key = getNotificationGroupKey(item);
    const accumulator = groups.get(key);
    if (!accumulator) {
      groups.set(key, {
        group: { key, latest: item, items: [item], messageCount: 1 },
        notificationIds: new Set([item.id]),
        messageIds: new Set([item.metadata?.message?.id ?? item.id]),
      });
    } else if (!accumulator.notificationIds.has(item.id)) {
      const { group, notificationIds, messageIds } = accumulator;
      notificationIds.add(item.id);
      messageIds.add(item.metadata?.message?.id ?? item.id);
      group.items.push(item);
      if (getNotificationTime(item) > getNotificationTime(group.latest)) {
        group.latest = item;
      }
      group.messageCount = messageIds.size;
    }
  }
  return [...groups.values()]
    .map(({ group }) => group)
    .sort(
      (a, b) => getNotificationTime(b.latest) - getNotificationTime(a.latest)
    );
};
